import { useRef, useCallback, useEffect, useState, KeyboardEvent, CSSProperties } from 'react'

interface EditableTextProps {
  value: string
  field: string
  editable?: boolean
  onEdit?: (field: string, value: string) => void
  className?: string
  tag?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'div'
  style?: CSSProperties
  multiline?: boolean
}

const baseEditableStyle: CSSProperties = {
  outline: 'none',
  border: '1px dashed transparent',
  borderRadius: 4,
  padding: '0 2px',
  margin: '0 -2px',
  transition: 'border-color 0.15s, background-color 0.15s',
  cursor: 'text',
  minHeight: '1.2em',
}

const hoverStyle: CSSProperties = {
  borderColor: 'rgba(var(--ds-brand-rgb, 0 0 0) / 0.3)',
}

const focusStyle: CSSProperties = {
  borderColor: 'rgba(var(--ds-brand-rgb, 0 0 0) / 0.5)',
  backgroundColor: 'rgba(var(--ds-brand-rgb, 0 0 0) / 0.05)',
}

export function EditableText({
  value,
  field,
  editable = false,
  onEdit,
  className = '',
  tag: Tag = 'span',
  style,
  multiline = false,
}: EditableTextProps) {
  const ref = useRef<HTMLElement | null>(null)
  const lastCommittedRef = useRef(value)
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current && value !== lastCommittedRef.current) {
      ref.current.innerText = value
      lastCommittedRef.current = value
    }
  }, [value])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
    if (!ref.current || !onEdit) return
    const newValue = ref.current.innerText.trim()
    if (newValue !== lastCommittedRef.current.trim()) {
      lastCommittedRef.current = newValue
      onEdit(field, newValue)
    }
  }, [field, onEdit])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (!multiline && e.key === 'Enter') {
        e.preventDefault()
        ref.current?.blur()
      }
      if (e.key === 'Escape') {
        if (ref.current) {
          ref.current.innerText = lastCommittedRef.current
        }
        ref.current?.blur()
      }
    },
    [multiline],
  )

  if (!editable) {
    return <Tag className={className} style={style}>{value}</Tag>
  }

  const mergedStyle: CSSProperties = {
    ...baseEditableStyle,
    ...(isHovered && !isFocused ? hoverStyle : undefined),
    ...(isFocused ? focusStyle : undefined),
    ...style,
  }

  return (
    <Tag
      ref={(el: HTMLElement | null) => { ref.current = el }}
      contentEditable
      suppressContentEditableWarning
      data-testid={`editable-text-${field}`}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={className}
      style={mergedStyle}
    >
      {value}
    </Tag>
  )
}
